import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormValues, counterpartyModel } from '~/entities/Counterparty'
import {
  FormButtons,
  formButtonsModel,
  FormProvider,
  NumericInput,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(counterpartyModel.formSchema),
    defaultValues,
  })

  const { reset } = form

  useEffect(() => {
    if (defaultValues) reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('form.information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='form.name'
              placeholder='form.enter name'
              name='name'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      <Container>
        <ContainerTitle>{t('form.account details')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={4}>
            <NumericInput
              label='form.inn'
              name='inn'
              placeholder='form.enter inn'
              skeletonShow={isLoading}
              readOnly={disabled}
              numericProps={{ thousandSeparator: '' }}
            />
          </Grid>
          <Grid xs={4}>
            <NumericInput
              label='form.kpp'
              name='kpp'
              placeholder='form.enter kpp'
              skeletonShow={isLoading}
              readOnly={disabled}
              numericProps={{ thousandSeparator: '' }}
            />
          </Grid>
          <Grid xs={4}>
            <NumericInput
              label='form.ogrn'
              name='ogrn'
              placeholder='form.enter ogrn'
              skeletonShow={isLoading}
              readOnly={disabled}
              numericProps={{ thousandSeparator: '' }}
            />
          </Grid>
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
